// src/pages/Stock/StockInventoryDetail.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stockService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    ArrowLeftIcon,
    ArrowPathIcon,
    PencilIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';
import AdjustStockModal from './AdjustStockModal';

const StockInventoryDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasAnyRole } = useAuth();
    const [stockItem, setStockItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adjustModalOpen, setAdjustModalOpen] = useState(false);

    useEffect(() => {
        fetchStockItem();
    }, [id]);

    const fetchStockItem = async () => {
        try {
            setLoading(true);
            const response = await stockService.getStockItem(id);
            setStockItem(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching stock item details:', err);
            setError('Failed to load stock details. Please try again later.');
            toast.error('Failed to load stock details');
        } finally {
            setLoading(false);
        }
    };

    const handleAdjustClick = () => {
        setAdjustModalOpen(true);
    };

    const confirmAdjust = async (stockId, newQuantity, notes) => {
        try {
            await stockService.adjustStock({
                                               stock_id: stockId,
                                               quantity: newQuantity,
                                               notes
                                           });

            fetchStockItem(); // Refresh the data
            toast.success('Stock adjusted successfully');
        } catch (error) {
            console.error('Error adjusting stock:', error);
            toast.error(error.response?.data?.message || 'Failed to adjust stock');
        } finally {
            setAdjustModalOpen(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    if (!stockItem) {
        return (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded shadow-md">
                <p className="font-bold">Not Found</p>
                <p>The requested stock item could not be found.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with Back Navigation */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" /> Back
                    </button>
                    <h1 className="text-2xl font-semibold text-gray-800">
                        Stock Details
                    </h1>
                </div>

                <div className="flex space-x-2">
                    <button
                        onClick={() => fetchStockItem()}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <ArrowPathIcon className="h-5 w-5 mr-2" /> Refresh
                    </button>

                    {hasAnyRole(['admin', 'production']) && (
                        <button
                            onClick={handleAdjustClick}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <PencilIcon className="h-5 w-5 mr-2" /> Adjust Stock
                        </button>
                    )}
                </div>
            </div>

            {/* Stock Summary Card */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Stock Information
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        Details about the product inventory.
                    </p>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Product SKU</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <Link to={`/products/${stockItem.product.id}`} className="text-primary-600 hover:text-primary-900 hover:underline">
                                    {stockItem.product.sku}
                                </Link>
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Product Name</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {stockItem.product.name}
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Variant</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    stockItem.variant === 'carbon'
                                    ? 'bg-indigo-100 text-indigo-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                    {stockItem.variant.charAt(0).toUpperCase() + stockItem.variant.slice(1)}
                                </span>
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Total Quantity</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {stockItem.quantity}
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Allocated Quantity</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {stockItem.allocated_quantity}
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Available Quantity</dt>
                            <dd className="mt-1 text-sm font-medium sm:mt-0 sm:col-span-2">
                                <span className={stockItem.available_quantity > 0 ? 'text-green-600' : 'text-red-600'}>
                                    {stockItem.available_quantity}
                                </span>
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {new Date(stockItem.updated_at).toLocaleString()}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Stock Allocations Section */}
            {stockItem.allocations && stockItem.allocations.length > 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6 bg-gray-50">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Stock Allocations
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Orders that have this stock allocated to them.
                        </p>
                    </div>

                    <ul className="divide-y divide-gray-200">
                        {stockItem.allocations.map((allocation) => (
                            <li key={allocation.id}>
                                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="ml-3">
                                                <p className="text-sm font-medium text-gray-900">
                                                    Order #{allocation.order?.order_number || 'N/A'}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {allocation.quantity} units • {allocation.status}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    Allocated on {new Date(allocation.created_at).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        <div>
                                            <Link
                                                to={`/orders/${allocation.order_id}`}
                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                            >
                                                View Order <ChevronRightIcon className="ml-1 h-4 w-4" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                 <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                     <div className="px-4 py-5 sm:px-6 bg-gray-50">
                         <h3 className="text-lg leading-6 font-medium text-gray-900">
                             Stock Allocations
                         </h3>
                     </div>
                     <div className="px-4 py-8 text-center text-gray-500">
                         <p>No allocations found for this stock item.</p>
                     </div>
                 </div>
             )}

            {/* Adjust Stock Modal */}
            <AdjustStockModal
                isOpen={adjustModalOpen}
                onClose={() => setAdjustModalOpen(false)}
                onConfirm={confirmAdjust}
                stockItem={stockItem}
            />
        </div>
    );
};

export default StockInventoryDetail;
